import request from 'api/request';
import {handleError, formatter} from 'helpers';

import * as alerts from './alertActions';
import reloadInventory from './inventoryActions';
import {invByLocationActionTypes} from './actionTypes';

export const updatePaging = ({pageNumber, pageSize}) => ({
  type: invByLocationActionTypes.INV_BY_LOCATION_PAGE_CHANGED,
  pageNumber,
  pageSize,
});

export const updateSorting = ({sortColumn, sortOrder}) => ({
  type: invByLocationActionTypes.INV_BY_LOCATION_SORT_CHANGED,
  sortColumn,
  sortOrder,
});

export const updateSelectedLocation = selectedLocationId => ({
  type: invByLocationActionTypes.INV_BY_LOCATION_SELECTED_CHANGED,
  selectedLocationId,
});

export const updateAllChecked = isChecked => dispatch =>
  dispatch({
    type: invByLocationActionTypes.INV_BY_LOCATION_ALL_CHECK_CHANGED,
    isChecked,
  });

export const updateItemChecked = itemId => dispatch =>
  dispatch({
    type: invByLocationActionTypes.INV_BY_LOCATION_ITEM_CHECK_CHANGED,
    itemId,
  });

export const getItemsByLocation = ({
  pageInfo,
  sortInfo,
  selectedLocationId,
}) => dispatch => {
  const {pageNumber, pageSize} = pageInfo;
  const {sortColumn, sortOrder} = sortInfo;

  dispatch({type: invByLocationActionTypes.INV_BY_LOCATION_LOADING});

  let url = `/InventoryLocations/${selectedLocationId}/InventoryItems`;
  url = `${url}?pageSize=${pageSize}&rowIndex=${(pageNumber - 1) * pageSize}`;

  if (sortColumn) {
    const sortCamelCase = `${sortColumn
      .charAt(0)
      .toLowerCase()}${sortColumn.substring(1)}`;
    const apiSortOrder = sortOrder === 'Descending' ? '-' : '';
    url += `&sort=${apiSortOrder}${sortCamelCase}`;
  }

  return request({url})
    .then(resp => {
      if (!resp || !resp.data) {
        dispatch(alerts.error('Unable to load Inventory Items.'));
      } else {
        const results = resp.data.results || [];

        dispatch({
          type: invByLocationActionTypes.GET_INV_ITEM_BY_LOCATION_ITEMS_SUCCESS,
          items: results.map(x => ({
            ...x,
            rate: formatter.formatRate(x.defaultSellPrice || 0),
            formattedDescription: formatter.formatDescription(x.description),
          })),
          totalRows: resp.data.totalRows ? resp.data.totalRows : 0,
          selectedLocationId,
        });
      }
    })
    .catch(error => {
      handleError('Unable to load Inventory Items', error, dispatch);
    });
};

export const loadInventoryByLocations = (
  dispatch,
  getState,
  useDefault = false,
) => {
  const state = getState();
  const {invByLocation, inventoryLocations} = state;
  let {selectedLocationId} = invByLocation;

  if (useDefault) {
    selectedLocationId = inventoryLocations.locations.find(x => x.isDefault)
      .inventoryLocationId;
    dispatch(updateSelectedLocation(selectedLocationId));
  }

  return dispatch(
    getItemsByLocation({
      pageInfo: invByLocation.pageInfo,
      sortInfo: invByLocation.sortInfo,
      selectedLocationId,
    }),
  );
};

export const removeFromLocation = (items, srcLocationId) => (
  dispatch,
  getState,
) => {
  dispatch({type: invByLocationActionTypes.REMOVE_INV_FROM_LOCATION_LOADING});
  return request({
    url: `/InventoryLocation/${srcLocationId}/InventoryItems`,
    body: {
      operation: 'delete',
      values: items.map(i => ({Id: i.id})),
    },
    method: 'PUT',
    batch: true,
  })
    .then(response => {
      if (!response || !response.data || !response.data.hasErrors) {
        dispatch(alerts.success('Delete successful'));
        reloadInventory(dispatch, getState);
      } else if (response.data.results) {
        response.data.results.forEach(i => {
          if (!i.success) {
            dispatch(alerts.error(`Error: ${i.errorMessage}`));
          }
        });
      }
      dispatch({
        type: invByLocationActionTypes.REMOVE_INV_FROM_LOCATION_COMPLETE,
      });
    })
    .catch(error => {
      handleError('Unable to remove inventory items', error, dispatch);
      dispatch({
        type: invByLocationActionTypes.REMOVE_INV_FROM_LOCATION_COMPLETE,
      });
    });
};

export const copyToLocation = (items, srcLocationId, dstLocationId) => (
  dispatch,
  getState,
) => {
  dispatch({type: invByLocationActionTypes.COPY_INV_TO_LOCATION_LOADING});
  return request({
    url: `/InventoryLocation/${srcLocationId}/InventoryItems`,
    body: {
      operation: 'copy',
      values: items.map(i => ({
        Id: i.id,
        partId: i.partId,
        inventoryLocationId: dstLocationId,
      })),
    },
    method: 'PUT',
    batch: true,
  })
    .then(response => {
      if (!response || !response.data) {
        dispatch(alerts.error('Error copying inventory items'));
      } else if (!response.data.hasErrors) {
        dispatch(alerts.success('Copy complete'));
        reloadInventory(dispatch, getState);
      } else if (response.data.results) {
        response.data.results.forEach(i => {
          if (!i.success) {
            dispatch(alerts.error(`Error: ${i.errorMessage}`));
          }
        });
      }
      dispatch({type: invByLocationActionTypes.COPY_INV_TO_LOCATION_COMPLETE});
    })
    .catch(error => {
      handleError('Unable to copy inventory items', error, dispatch);
      dispatch({type: invByLocationActionTypes.COPY_INV_TO_LOCATION_COMPLETE});
    });
};

export const moveItems = (items, srcLocationId, dstLocationId) => (
  dispatch,
  getState,
) => {
  dispatch({type: invByLocationActionTypes.MOVE_ITEMS.LOADING, payload: true});

  return request({
    url: `/inventorylocation/${srcLocationId}/inventoryitems/move`,
    body: {
      NewLocationId: dstLocationId,
      values: items.map(i => ({
        Id: i.id,
        Name: i.name,
        partId: i.partId,
        QuantityOnHand: i.moveCount,
      })),
    },
    method: 'PUT',
    batch: true,
  })
    .then(response => {
      if (!response || !response.data) {
        dispatch(alerts.error('Error moving inventory items'));
      } else if (!response.data.hasErrors) {
        dispatch(alerts.success('Move complete'));

        const checkedItems = getState().invByLocation.items.filter(
          item => item.isChecked,
        );

        reloadInventory(dispatch, getState, {
          byLocationCallback: () =>
            checkedItems.map(item => dispatch(updateItemChecked(item.id))),
        });
      } else if (response.data.results) {
        response.data.results.forEach(i => {
          if (!i.success) {
            dispatch(alerts.error(`Error: ${i.errorMessage}`));
          }
        });
      }

      dispatch({
        type: invByLocationActionTypes.MOVE_ITEMS.COMPLETE,
        payload: false,
      });
    })
    .catch(error => {
      handleError('Unable to move inventory items', error, dispatch);
      dispatch({
        type: invByLocationActionTypes.MOVE_ITEMS.COMPLETE,
        payload: false,
      });
    });
};

export const getLocationSummary = locationId => dispatch => {
  dispatch({type: invByLocationActionTypes.INV_BY_LOCATION_SUMMARY_LOADING});
  return request({
    url: `/InventoryLocations/${locationId}/Summary`,
  })
    .then(response => {
      dispatch({
        type: invByLocationActionTypes.INV_BY_LOCATION_SUMMARY_LOADED,
        summary: response.data,
      });
    })
    .catch(error => {
      handleError(
        'Unable to load inventory location information',
        error,
        dispatch,
      );
    });
};

export const updateLocationSummary = (dispatch, getState) => {
  const state = getState();
  const {invByLocation} = state;
  dispatch(getLocationSummary(invByLocation.selectedLocationId));
};

export const updateLocationItem = (item, property, value) => (
  dispatch,
  getState,
) => {
  if (
    (property === 'reOrderPoint' || property === 'quantityOnHand') &&
    value.toString().length > 13
  ) {
    handleError(
      'Unable to update inventory item',
      {message: 'The value must be less than 13 characters long.'},
      dispatch,
    );

    return Promise.reject();
  }

  const body = {
    id: item ? item.id : 0,
    reOrderPoint: item ? item.reOrderPoint : 0,
    quantityOnHand: item ? item.quantityOnHand : 0,
    quantityAsOfDate: item ? item.quantityAsOfDate : null,
  };
  body[property] = value;

  const url = `/inventoryitems/${item.partId}/locations/${
    item.inventoryLocationId
  }`;

  return request({
    url,
    method: item ? 'PUT' : 'POST',
    body,
  })
    .then(result => {
      dispatch({
        type: invByLocationActionTypes.INV_BY_LOCATIONS_ITEM_SUBMIT,
        id: item.id,
        property,
        value: result.data[property],
      });
      reloadInventory(dispatch, getState, {doLoadInventoryByLocation: false});
      return result;
    })
    .catch(error => {
      handleError('Unable to to update item', error, dispatch);
      return error;
    });
};

export const exportCSV = inventoryLocationId => dispatch => {
  return request({
    url: `/CSV/InventoryLocations/${inventoryLocationId}/InventoryItems`,
    headers: {contentType: 'text/csv'},
  })
    .then(result => {
      if (result.status === 200) {
        dispatch(alerts.success(result.data.message));
        return true;
      }
      return false;
    })
    .catch(error => handleError('Error exporting to CSV', error, dispatch));
};



// WEBPACK FOOTER //
// ./src/actions/inventoryByLocationActions.js